'use client';

import RecommendReviewCard from '@/app/(home)/components/recommend-review-card';
import FullWidthSection from '@/components/full-width-section';
import ImageOptimizer from '@/components/image-optimizer';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { useFeaturedStoreReviewList } from '@/services/query/store-review.query';
import Autoplay from 'embla-carousel-autoplay';
import { useEffect, useState } from 'react';

const RecommendReviewSection = () => {
  const { data: featuredStoreReviewList } = useFeaturedStoreReviewList();
  const featuredStoreReviews = featuredStoreReviewList?.data || [];

  const [pcApi, setPcApi] = useState<CarouselApi>();
  const [mobileApi, setMobileApi] = useState<CarouselApi>();
  const [pcCurrentSlide, setPcCurrentSlide] = useState(0);
  const [mobileCurrentSlide, setMobileCurrentSlide] = useState(0);

  useEffect(() => {
    if (!mobileApi && !pcApi) return;

    const cleanups: (() => void)[] = [];

    if (mobileApi) {
      const onMobileSelect = () => {
        setMobileCurrentSlide(mobileApi.selectedScrollSnap());
      };
      mobileApi.on('select', onMobileSelect);
      onMobileSelect(); // 초기 설정
      cleanups.push(() => mobileApi.off('select', onMobileSelect));
    }

    if (pcApi) {
      const onPcSelect = () => {
        setPcCurrentSlide(pcApi.selectedScrollSnap());
      };
      pcApi.on('select', onPcSelect);
      onPcSelect(); // 초기 설정
      cleanups.push(() => pcApi.off('select', onPcSelect));
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [mobileApi, pcApi]);

  const handlePrevClick = () => {
    pcApi?.scrollPrev();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  const handleNextClick = () => {
    pcApi?.scrollNext();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  return (
    <FullWidthSection className="bg-bg-grayblue">
      {/**웹 */}
      <div className="desktop:block hidden">
        {featuredStoreReviews?.length > 0 && (
          <section className="mx-auto pt-20 pb-30">
            <div className="relative">
              <Carousel
                setApi={setPcApi}
                opts={{
                  align: 'center',
                  loop: true,
                  slidesToScroll: 1,
                }}
                plugins={[
                  Autoplay({
                    delay: 3000,
                    stopOnMouseEnter: true,
                    stopOnInteraction: false,
                  }),
                ]}
              >
                <CarouselContent className="mx-auto -ml-[529px]">
                  {featuredStoreReviews.map((review, idx) => (
                    // 현재 슬라이드와 다음 슬라이드와 관련된 투명도 조절 (현재 슬라이드가 마지막일 경우 0으로 순환)
                    <CarouselItem
                      key={idx}
                      className={`h-[304px] w-[513px] flex-shrink-0 basis-[513px] ${
                        idx === pcCurrentSlide ||
                        idx ===
                          (pcCurrentSlide + 1) % featuredStoreReviews.length
                          ? 'opacity-100'
                          : 'opacity-50'
                      }`}
                    >
                      <RecommendReviewCard {...review} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className="web-w relative mx-auto">
              <Button
                onClick={handlePrevClick}
                disabled={!pcApi}
                className="absolute -top-[150px] left-10 z-1 -translate-y-1/2 shadow-none"
              >
                <ImageOptimizer
                  src="/images/icons/icon_slide_left.svg"
                  alt="이전 슬라이드"
                  width={55}
                  height={55}
                  className="cursor-pointer rounded-full hover:bg-gray-100"
                />
              </Button>
              <Button
                onClick={handleNextClick}
                disabled={!pcApi}
                className="absolute -top-[150px] right-10 z-1 -translate-y-1/2 shadow-none"
              >
                <ImageOptimizer
                  src="/images/icons/icon_slide_right.svg"
                  alt="다음 슬라이드"
                  width={55}
                  height={55}
                  className="cursor-pointer rounded-full hover:bg-gray-100"
                />
              </Button>
            </div>
          </section>
        )}
      </div>

      {/**모바일 */}
      <section className="desktop:hidden bg-bg-grayblue overflow-hidden pt-9 pb-20">
        <Carousel
          setApi={setMobileApi}
          opts={{
            loop: true,
            align: 'start',
            slidesToScroll: 1,
            containScroll: 'trimSnaps',
          }}
          plugins={[
            Autoplay({
              delay: 3000,
              stopOnMouseEnter: true,
              stopOnInteraction: false,
            }),
          ]}
        >
          <CarouselContent className="mx-auto">
            {featuredStoreReviews.map((review, idx) => (
              <CarouselItem
                key={idx}
                className={cn(
                  'h-[251px] w-[260px] flex-shrink-0 basis-[260px]',
                  idx === mobileCurrentSlide && 'pl-6',
                )}
              >
                <RecommendReviewCard {...review} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </section>
    </FullWidthSection>
  );
};

export default RecommendReviewSection;
